/**
 * PopEditor Component
 *
 * Markdown editor for POP content with preview toggle.
 * Story 6-5
 */

import { useState } from 'react';
import { PopViewer } from './PopViewer';

interface PopEditorProps {
  content: string;
  onChange: (content: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export function PopEditor({ content, onChange, onSave, onCancel, isSaving }: PopEditorProps) {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-gray-200 px-4 py-2 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(false)}
            className={`px-3 py-1 text-sm rounded ${
              !showPreview
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Bewerken
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className={`px-3 py-1 text-sm rounded ${
              showPreview
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Voorbeeld
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            data-testid="save-pop-button"
            onClick={onSave}
            disabled={isSaving}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4">
        {showPreview ? (
          <div className="min-h-[400px]">
            {content ? (
              <PopViewer content={content} />
            ) : (
              <p className="text-gray-400 italic">Geen inhoud om weer te geven</p>
            )}
          </div>
        ) : (
          <textarea
            data-testid="pop-content"
            value={content}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-[400px] px-3 py-2 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              font-mono text-sm resize-none"
            placeholder="# Mijn Ontwikkelingsplan

## Doelen voor dit jaar
- Doel 1
- Doel 2

## Ontwikkelgebieden
Beschrijf hier je ontwikkelgebieden..."
          />
        )}
      </div>

      {/* Markdown help */}
      {!showPreview && (
        <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 text-xs text-gray-500">
          <strong>Markdown tips:</strong> # voor kop, **vet**, *cursief*, - voor lijst,
          [link](url)
        </div>
      )}
    </div>
  );
}
